import React, { useState, useEffect } from "react";
import api from "../../api/api";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  MenuItem,
  Typography,
  Paper,
  Grid,
} from "@mui/material";
import Swal from "sweetalert2";

const COLORS = {
  black: "#000000",
  white: "#ffffff",
  deepYellow: "#D4A017",
  orange: "#F97316",
  camel: "#C19A6B",
  lightCamel: "#F5EBDD",
  lightGray: "#F7F7F7",
  border: "#E0E0E0",
  text: "#222222",
};

const LessonPlan = () => {
  const [formData, setFormData] = useState({
    subject: "",
    grade_class: "",
    period: "",
    topic: "",
    objectives: "",
    content: "",
    teaching_method: "",
    assessment: "",
    week: "",
  });

  const [subjects, setSubjects] = useState([]);
  const [gradeClasses, setGradeClasses] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);

  const accessToken = localStorage.getItem("access");

  useEffect(() => {
    api
      .get("/assigned-subjects/", {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => setSubjects(res.data))
      .catch((err) => console.error("Error loading subjects:", err));

    api
      .get("/assigned-grade-classes/", {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => setGradeClasses(res.data))
      .catch((err) => console.error("Error loading classes:", err));

    api
      .get("/api/users/periods/", {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => setPeriods(res.data))
      .catch((err) => console.error("Error loading periods:", err));
  }, [accessToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post("/lesson-plans/", formData, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      Swal.fire(
        "Success",
        "Lesson plan submitted successfully!",
        "success"
      );

      setFormData({
        subject: "",
        grade_class: "",
        period: "",
        topic: "",
        objectives: "",
        content: "",
        teaching_method: "",
        assessment: "",
        week: "",
      });
    } catch (err) {
      const errorData = err.response?.data || {};

      let errorMessage = "";

      for (const key in errorData) {
        errorMessage += `${key}: ${errorData[key]}\n`;
      }

      Swal.fire(
        "Error",
        errorMessage || "Submission failed.",
        "error"
      );
    } finally {
      setTimeout(() => setLoading(false), 3000);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.value,
    });
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: COLORS.white,
      transition: "all 0.2s ease",
      "& fieldset": {
        borderColor: COLORS.border,
      },
      "&:hover fieldset": {
        borderColor: COLORS.camel,
      },
      "&.Mui-focused fieldset": {
        borderColor: COLORS.deepYellow,
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: COLORS.deepYellow,
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: COLORS.lightGray,
        p: { xs: 1.5, sm: 3, md: 4 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            borderRadius: "16px",
            border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.white,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${COLORS.black}, #252525)`,
              px: { xs: 2, sm: 4 },
              py: { xs: 2.5, sm: 3 },
              borderBottom: `5px solid ${COLORS.deepYellow}`,
            }}
          >
            <Typography
              sx={{
                color: COLORS.white,
                fontSize: { xs: "1.35rem", sm: "1.7rem" },
                fontWeight: 800,
                letterSpacing: "0.3px",
              }}
            >
              Submit Lesson Plan
            </Typography>

            <Typography
              sx={{
                color: "#D6D6D6",
                mt: 0.5,
                fontSize: "0.9rem",
              }}
            >
              Complete the form below to submit your lesson plan.
            </Typography>
          </Box>

          {/* Form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
            }}
          >
            <Grid container spacing={2.5}>
              {[
                {
                  label: "Subject",
                  key: "subject",
                  data: subjects,
                },
                {
                  label: "Grade Class",
                  key: "grade_class",
                  data: gradeClasses,
                },
                {
                  label: "Period",
                  key: "period",
                  data: periods,
                },
              ].map(({ label, key, data }) => (
                <Grid item xs={12} md={4} key={key}>
                  <TextField
                    select
                    fullWidth
                    required
                    label={label}
                    value={formData[key]}
                    onChange={handleChange(key)}
                    sx={inputStyles}
                  >
                    {data.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ))}

              {["topic", "week"].map((field) => (
                <Grid item xs={12} md={6} key={field}>
                  <TextField
                    fullWidth
                    required
                    label={
                      field.charAt(0).toUpperCase() +
                      field.slice(1)
                    }
                    value={formData[field]}
                    onChange={handleChange(field)}
                    sx={inputStyles}
                  />
                </Grid>
              ))}

              {[
                "objectives",
                "content",
                "teaching_method",
                "assessment",
              ].map((field) => (
                <Grid item xs={12} key={field}>
                  <TextField
                    multiline
                    required
                    fullWidth
                    minRows={3}
                    label={field
                      .replace("_", " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                    value={formData[field]}
                    onChange={handleChange(field)}
                    sx={inputStyles}
                  />
                </Grid>
              ))}

              {/* Submit Button */}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  disabled={loading}
                  startIcon={
                    loading && (
                      <CircularProgress
                        size={20}
                        color="inherit"
                      />
                    )
                  }
                  sx={{
                    mt: 1,
                    height: 52,
                    borderRadius: "10px",
                    backgroundColor: COLORS.black,
                    color: COLORS.white,
                    fontSize: "1rem",
                    fontWeight: 800,
                    textTransform: "none",
                    boxShadow: "0 5px 14px rgba(0,0,0,0.18)",
                    transition: "all 0.25s ease",
                    "&:hover": {
                      backgroundColor: COLORS.deepYellow,
                      color: COLORS.black,
                      boxShadow:
                        "0 7px 18px rgba(212,160,23,0.35)",
                    },
                    "&:disabled": {
                      backgroundColor: "#777",
                      color: COLORS.white,
                    },
                  }}
                >
                  {loading
                    ? "Submitting..."
                    : "Submit Lesson Plan"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default LessonPlan;